import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  PlugZap,
  FileCheck2,
  Send,
  Inbox,
  FileSpreadsheet,
  Layers,
  RefreshCw,
  BellRing,
  ShieldCheck,
  Radio,
  Network,
  Search,
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { publishedEinvCountries, statusClasses, EINV_REGIONS } from '@/data/einvoicingCountries';
import EinvoicingFlag from '@/components/einvoicing/EinvoicingFlag';
/* ------------------------------------------------------------------ */
/*  Content                                                            */
/* ------------------------------------------------------------------ */
const MODELS = [
  {
    icon: ShieldCheck,
    title: 'Clearance',
    text: 'The invoice goes to the tax authority first. It is checked and approved, and only then does it count as valid. Examples: Italy, Poland, Greece, Romania.',
  },
  {
    icon: Radio,
    title: 'Real-time reporting',
    text: 'The invoice is issued as usual, and the data is reported to the authority at the same time or very soon after. Example: Hungary.',
  },
  {
    icon: Network,
    title: 'Network exchange',
    text: 'Invoices travel between the two businesses over a standard network such as Peppol, using agreed formats. Example: Belgium.',
  },
  {
    icon: Search,
    title: 'Post-audit',
    text: 'Businesses exchange structured invoices directly. The authority does not approve them but can check them later. Example: Germany.',
  },
];
const STEPS = [
  {
    icon: PlugZap,
    title: 'Connect to your ERP',
    text: 'Taxera sits inside your ERP landscape (SAP, Oracle, Microsoft Dynamics and others). No file exports, no manual uploads.',
  },
  {
    icon: FileCheck2,
    title: 'Create the invoice in the right format',
    text: 'Each invoice is built in the structure its country requires and checked against the official rules before it leaves.',
  },
  {
    icon: Send,
    title: 'Send it the right way',
    text: 'The invoice goes through the channel the country uses, such as the tax authority platform or a network, and you see its status.',
  },
  {
    icon: Inbox,
    title: 'Receive and keep',
    text: 'Incoming e-invoices come back into your ERP, and everything is archived with a full audit trail.',
  },
];
const BENEFITS = [
  {
    icon: FileSpreadsheet,
    title: 'No spreadsheets in the middle',
    text: 'Invoice data goes from your ERP to the customer and the tax authority without anyone re-keying or patching numbers.',
  },
  {
    icon: Layers,
    title: 'One integration for everything',
    text: 'e-Invoicing uses the same data as your SAF-T files and VAT returns, so the numbers agree across all of them.',
  },
  {
    icon: RefreshCw,
    title: 'Reconciled by design',
    text: 'Invoices are matched to your source transactions and filed returns, which is exactly what authorities now do on their side.',
  },
  {
    icon: BellRing,
    title: 'Format changes are our problem',
    text: 'When a country changes its format, deadline or portal, we track it and update the platform. You do not have to rebuild anything.',
  },
];
const FAQS = [
  {
    q: 'What is e-Invoicing?',
    a: 'e-Invoicing means issuing and receiving invoices as structured electronic data (usually XML) that software can read automatically. A PDF sent by email is not an e-invoice in this sense. In many countries the tax authority also receives, checks or approves the invoice data.',
  },
  {
    q: 'Which countries require e-Invoicing?',
    a: 'Requirements differ by country in scope, format, channel and deadlines, and they keep changing. The country list on this page shows the status of each country we have reviewed. Our free Global Tax Tracker follows changes country by country.',
  },
  {
    q: 'What is the difference between clearance, real-time reporting and Peppol?',
    a: 'With clearance, the tax authority approves each invoice before it is valid. With real-time reporting, the invoice is issued normally and its data is reported at once. With Peppol, invoices are exchanged between businesses over a standard network. Some countries combine these models.',
  },
  {
    q: 'What does ViDA mean for e-Invoicing in the EU?',
    a: 'ViDA (VAT in the Digital Age) is the EU package that makes structured e-Invoicing and digital reporting the standard for cross-border business transactions in the EU from 1 July 2030. It also lets member states introduce their own domestic e-Invoicing rules earlier, which is why national deadlines keep arriving.',
  },
  {
    q: 'How does Taxera create e-invoices from SAP or another ERP?',
    a: "Taxera's middleware connects to your ERP, builds each invoice in the format its country requires, validates it, sends it through the right channel and returns the status to your ERP. Incoming e-invoices come back the same way.",
  },
  {
    q: 'Do we need a separate tool for every country?',
    a: 'No. Taxera is one platform with one ERP integration. Country formats and rules are handled inside the platform, alongside your SAF-T and VAT reporting.',
  },
];
const RELATED = [
  { to: '/tracker/einvoicing', label: 'Live e-Invoicing changes', note: 'Free Global Tax Tracker' },
  { to: '/solutions/saf-t', label: 'SAF-T reporting', note: 'Same data, same integration' },
  { to: '/sap-s4hana', label: 'SAP S/4HANA and tax compliance', note: 'Migration without compliance gaps' },
  { to: '/blog', label: 'Latest articles and updates', note: 'Regulatory analysis from our team' },
];
/* ------------------------------------------------------------------ */
/*  Small pieces                                                       */
/* ------------------------------------------------------------------ */
const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`einv-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`einv-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm sm:text-base text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
};
const STATUS_FILTERS = [
  { key: 'all', label: 'All statuses' },
  { key: 'mandatory', label: 'Mandatory' },
  { key: 'upcoming', label: 'Starting soon' },
  { key: 'voluntary', label: 'Voluntary or B2G only' },
];
const statusKey = (status) => {
  if (status === 'upcoming') return 'upcoming';
  if (status === 'voluntary') return 'voluntary';
  return 'mandatory';
};
const statusDot = (status) => {
  if (status === 'upcoming') return 'bg-amber-400';
  if (status === 'voluntary') return 'bg-slate-400';
  return 'bg-[#0066CC]';
};
const chipClass = (active) =>
  `text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
    active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
  }`;
/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
const EinvoicingSolutionPage = () => {
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const all = publishedEinvCountries();
  const regions = EINV_REGIONS.filter((r) => all.some((c) => c.region === r));
  const visibleCountries = all.filter(
    (c) => (regionFilter === 'all' || c.region === regionFilter) && (statusFilter === 'all' || statusKey(c.status) === statusFilter)
  );
  // Page-level structured data (FAQ + breadcrumb). Removed when leaving the page.
  useEffect(() => {
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://taxera.tech/' },
          { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://taxera.tech/products' },
          { '@type': 'ListItem', position: 3, name: 'e-Invoicing', item: 'https://taxera.tech/solutions/e-invoicing' },
        ],
      },
    ];
    const nodes = schemas.map((schema, i) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-page-jsonld', `einv-${i}`);
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
  }, []);
  return (
    <PageLayout>
      <div data-testid="einv-solution-page">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pt-10 pb-16">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-8" data-testid="einv-breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-slate-700">Solutions</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-600">e-Invoicing</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#14A3B8] mb-4">e-Invoicing</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] max-w-4xl">
              e-Invoicing that every country accepts, straight from your ERP.{' '}
              <span className="text-[#0066CC]">In every country.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              More and more tax authorities want invoices as structured data, sent through their own systems. Taxera creates,
              checks, sends and receives them directly from your live ERP data, so your team stops chasing portals and spreadsheets.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                data-testid="einv-cta-demo"
              >
                Book a demo <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#countries"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0066CC] text-slate-800 font-semibold px-6 py-3 rounded-xl transition-colors"
                data-testid="einv-cta-countries"
              >
                See requirements by country
              </a>
            </div>
          </motion.div>
        </section>
        {/* Requirements by country: directly under the hero */}
        {all.length > 0 && (
          <section id="countries" className="bg-slate-50 border-y border-slate-100 scroll-mt-24" data-testid="einv-countries">
            <div className="max-w-6xl mx-auto px-6 py-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">e-Invoicing requirements by country</h2>
              <p className="text-slate-600 mb-8 max-w-2xl">
                Every country does e-Invoicing a little differently. Pick yours to see who has to issue, the key dates and how the process works.
              </p>
              <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Filter by region" data-testid="einv-region-filter">
                <button type="button" onClick={() => setRegionFilter('all')} aria-pressed={regionFilter === 'all'} className={chipClass(regionFilter === 'all')} data-testid="einv-region-all">
                  All regions
                </button>
                {regions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRegionFilter(r)}
                    aria-pressed={regionFilter === r}
                    className={chipClass(regionFilter === r)}
                    data-testid={`einv-region-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <div className="mb-6 flex flex-wrap gap-2" role="group" aria-label="Filter by status" data-testid="einv-status-filter">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setStatusFilter(f.key)}
                    aria-pressed={statusFilter === f.key}
                    className={chipClass(statusFilter === f.key)}
                    data-testid={`einv-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {visibleCountries.length === 0 && (
                <p className="text-slate-500 py-6" data-testid="einv-no-results">No countries match these filters yet.</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visibleCountries.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/solutions/e-invoicing/${c.slug}`}
                    className="group relative flex flex-col justify-between min-h-[190px] rounded-3xl bg-white border border-slate-200 p-6 hover:border-[#0066CC] hover:shadow-lg hover:shadow-blue-900/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0066CC] transition-all"
                    data-testid={`einv-country-link-${c.slug}`}
                  >
                    <div className="flex items-start justify-between">
                      <EinvoicingFlag code={c.code} name={c.name} className="h-14 w-14" />
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 group-hover:bg-[#0066CC] group-hover:border-[#0066CC] group-hover:text-white transition-colors">
                        <ArrowRight className="w-4 h-4 -rotate-45 group-hover:rotate-0 transition-transform" />
                      </span>
                    </div>
                    <div className="mt-8">
                      <div className="font-heading text-2xl font-semibold text-slate-900 leading-tight">{c.name}</div>
                      <div className="mt-1 text-sm text-slate-500">{c.shortName || c.model}</div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <span className={`h-2 w-2 rounded-full ${statusDot(c.status)}`} aria-hidden="true" />
                        {c.statusLabel}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <p className="mt-8 text-sm text-slate-500">
                Want to follow changes as they happen? See the <Link to="/tracker/einvoicing" className="text-[#0066CC] font-semibold hover:underline" data-testid="einv-tracker-link">Global Tax Tracker</Link>.
              </p>
            </div>
          </section>
        )}
        {/* What it is (with the four models) */}
        <section className="bg-slate-50 border-y border-slate-100" data-testid="einv-what">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">What is e-Invoicing, in plain words?</h2>
              </div>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  An e-invoice is an invoice sent as structured data, not as a PDF or a paper copy. Software on both sides can read it
                  without anyone typing it in again. In many countries the tax authority sees it too, and in some it has to approve it first.
                </p>
                <p>
                  Each country decides its own format, channel, deadlines and exceptions, and the rules keep changing. For a company
                  active in several countries, that quickly becomes a patchwork of portals, connectors and manual workarounds.
                </p>
              </div>
            </div>
            <h3 className="mt-12 mb-6 text-lg font-bold text-slate-900" data-testid="einv-models">Four ways countries do it</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {MODELS.map((m, i) => (
                <div key={m.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`einv-model-${i + 1}`}>
                  <span className="w-9 h-9 rounded-lg bg-[#0066CC]/10 flex items-center justify-center mb-4">
                    <m.icon className="w-5 h-5 text-[#0066CC]" strokeWidth={1.5} />
                  </span>
                  <h4 className="text-base font-bold text-slate-900 mb-2">{m.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* How it works */}
        <section className="max-w-6xl mx-auto px-6 py-16" data-testid="einv-how">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">How Taxera does it</h2>
          <p className="text-slate-600 mb-10 max-w-2xl">From your ERP to your customer and the tax authority, in four steps you do not have to run by hand.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="rounded-2xl border border-slate-200 bg-white p-6"
                data-testid={`einv-step-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#0066CC]" strokeWidth={1.5} />
                  </span>
                  <span className="text-xs font-semibold text-slate-400">STEP {i + 1}</span>
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </section>
        {/* Why it makes life easier */}
        <section className="bg-slate-50 border-y border-slate-100" data-testid="einv-benefits">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">What changes for your team</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07 }}
                  className="flex gap-4 rounded-2xl bg-white border border-slate-200 p-6"
                  data-testid={`einv-benefit-${i + 1}`}
                >
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-[#14A3B8]/10 flex items-center justify-center">
                    <b.icon className="w-5 h-5 text-[#14A3B8]" strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">{b.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 py-16" data-testid="einv-faq">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">e-Invoicing questions, answered</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </section>
        {/* Related */}
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="einv-related">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Keep reading</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {RELATED.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="group rounded-xl border border-slate-200 p-5 hover:border-[#0066CC]/50 hover:shadow-md transition-all"
                data-testid={`einv-related-${r.to.replace(/\//g, '-').replace(/^-/, '')}`}
              >
                <div className="text-sm font-semibold text-slate-900 group-hover:text-[#0066CC] transition-colors">{r.label}</div>
                <div className="text-xs text-slate-500 mt-1">{r.note}</div>
              </Link>
            ))}
          </div>
        </section>
        {/* Final CTA */}
        <section className="bg-[#1A1A1A] text-white" data-testid="einv-final-cta">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold">See e-Invoicing running on your own ERP data</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">
              Book a short demo. We will show you the full path from your ERP to your customer and the tax authority.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-7 py-3 rounded-xl transition-colors"
              data-testid="einv-final-demo"
            >
              Book a demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};
export default EinvoicingSolutionPage;
